import { Injectable } from '@angular/core';
import { AppConstant } from '@app/core/constant/app-constant';

// Extend Window so TypeScript knows fbq exists at compile time
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

export interface FbqPurchaseParams {
  value: number;
  currency: string;
  content_name?: string;
  content_type?: string;
  content_ids?: string[];
}

export interface FbqViewContentParams {
  content_name: string;
  content_type?: string;
  content_ids?: string[];
  value?: number;
  currency?: string;
}

@Injectable({ providedIn: 'root' })
export class FacebookPixelService {
  private readonly isEnabled =
    AppConstant.EnabledAnalyticsServices.facebookPixel;

  /**
   * Internal wrapper — guards against SSR (no window) and
   * the pixel script not yet being loaded.
   */
  private fbq(...args: unknown[]): void {
    if (!this.isEnabled) return;
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq(...args);
    }
  }

  /** Fire on every SPA route change (called from AppComponent). */
  trackPageView(): void {
    this.fbq('track', 'PageView');
  }

  /** Fire after a user completes registration or OTP login. */
  trackCompleteRegistration(method = 'OTP'): void {
    this.fbq('track', 'CompleteRegistration', { method });
  }

  /**
   * Fire when the payment popup opens — before the user
   * actually completes the transaction.
   */
  trackInitiateCheckout(
    value: number,
    currency = 'INR',
    contentName?: string,
  ): void {
    this.fbq('track', 'InitiateCheckout', {
      value,
      currency,
      content_name: contentName,
    });
  }

  /** Fire on payment success page. */
  trackPurchase(params: FbqPurchaseParams): void {
    this.fbq('track', 'Purchase', params);
  }

  /** Fire when a movie/web-series detail page loads. */
  trackViewContent(params: FbqViewContentParams): void {
    this.fbq('track', 'ViewContent', params);
  }

  /** Fire when a filmmaker submits a film for upload. */
  trackLead(contentName?: string): void {
    this.fbq('track', 'Lead', { content_name: contentName });
  }

  /** Fire when a user performs a search. */
  trackSearch(searchString: string): void {
    this.fbq('track', 'Search', { search_string: searchString });
  }

  /**
   * Generic escape hatch for custom events not covered
   * by the standard Facebook event set above.
   */
  trackCustom(eventName: string, params?: Record<string, unknown>): void {
    this.fbq('trackCustom', eventName, params);
  }
}
